import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form, Button, Header, Dropdown, Grid, Loader,
  CheckboxProps, DropdownProps, DropdownItemProps, Dimmer, Checkbox
} from 'semantic-ui-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import _ from 'lodash';
import axiosInstance from '../../helpers/axiosInstance';
import { utils } from '../../helpers/utils';
import { IdNameViewModel } from '../../types/ServerDTO';
import { UserModel } from '../../types/UserDTO';
import UserInfo from '../Common/SharedComponents/UserInfo';
import AppContext, { token_config } from '../../AppContext';
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';

type FormErrorFields = {
  roleId: string;
  authenticationTypeId: string;
  firstName: string;
  lastName: string;
  email: string;
  officeId: string;
};

export const UserForm: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useContext(AppContext);

  useEffect(() => {
    if (userInfo !== undefined) {
      const privileges: string[] = userInfo ? userInfo.Privileges.split(',') : [];
      if (!privileges.includes(token_config.MANAGE_USERS)) {
        navigate('/');
      }
    }
  }, [userInfo, navigate]);

  const { id } = useParams();

  let userId = id ? parseInt(id) : 0;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<UserModel>({} as UserModel);
  const [disabledOfficeDropdown, setDisabledOfficeDropdown] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<FormErrorFields>({
    roleId: '',
    authenticationTypeId: '',
    firstName: '',
    lastName: '',
    email: '',
    officeId: ''
  });

  useEffect(() => {
    if (userId > 0) {
      document.title = 'Edit User - Adjure';
    } else {
      document.title = 'Create User - Adjure';
    }
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      let apiUrl = `/Auth/GetCreateUserRequest`;
      if (userId > 0) {
        apiUrl = `/AdminSetting/GetUser/${userId}`;
      }
      await axiosInstance.get(apiUrl).then(({ data, status }) => {
        if (status === 200) {
          setFormValues(data);
        }
        setIsLoading(false);
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleRadioButtonChange = (event: React.FormEvent<HTMLInputElement>, data: any, field: string) => {
    if (field === 'roleId') {
      setDisabledOfficeDropdown(data.value === 1 ? true : false);
      setFormValues({ ...formValues, [field]: data.value, officeId: data.value === 1 ? 1 : 0 });
    } else if (field === 'isAssignToAllowed') {
      setFormValues({ ...formValues, [field]: data.value === 1 ? true : false });
    } else {
      setFormValues({ ...formValues, [field]: data.value });
    }
  };

  const handleCheckboxChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, field: string) => {
    setFormValues({ ...formValues, [field]: data.checked });
  };

  const handleDropdownChange = (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    const { name, value } = data;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleDropdownBlur = (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    const { name, value } = data;
    validate(false, name, value);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    validate(false, name, value);
  };

  const validate = async (validateAll: boolean, fieldName: string | null = null, value: any | null = null): Promise<FormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: FormErrorFields = { ...formErrors };
      try {
        if (validateAll) {
          errors = {
            roleId: !formValues?.roleId ? 'Role is required' : '',
            authenticationTypeId: !formValues?.authenticationTypeId ? 'Authentication type is required' : '',
            firstName: !formValues?.firstName ? 'First name is required' : '',
            lastName: !formValues?.lastName ? 'Last name is required' : '',
            email: !formValues?.email ? 'Email is required' : (formValues?.authenticationTypeId === 1 ? (!utils.isValidMinneapolismnEmail(formValues.email) ? 'Please provide a valid Minneapolismn email' : '') : (!utils.isValidEmail(formValues.email) ? 'Please provide a valid email' : '')),
            officeId: !formValues?.officeId ? 'Office is required' : '',
          };
        } else {
          switch (fieldName) {
            case 'roleId':
              errors.roleId = !value ? 'Role is required' : '';
              break;
            case 'authenticationTypeId':
              errors.authenticationTypeId = !value ? 'Authentication type is required' : '';
              break;
            case 'firstName':
              errors.firstName = !value ? 'First name is required' : '';
              break;
            case 'lastName':
              errors.lastName = !value ? 'Last name is required' : '';
              break;
            case 'email':
              errors.email = !value ? 'Email is required' : (formValues?.authenticationTypeId === 1 ? (!utils.isValidMinneapolismnEmail(value) ? 'Please provide a valid Minneapolismn email' : '') : (!utils.isValidEmail(value) ? 'Please provide a valid email' : ''));
              break;
            case 'officeId':
              errors.officeId = !value ? 'Office is required' : '';
              break;
            default:
              break;
          }
        }
        setFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  const handleSubmit = async () => {
    let apiUrl = `/Auth/CreateUser`;
    if (userId > 0) {
      apiUrl = `/AdminSetting/UpdateUser`;
    }
    const errors = await validate(true);
    const hasErrors = Object.values(errors).some((error) => error);
    if (!hasErrors) {
      // handle form submission 
      let payload = { ...formValues };
      payload.masterData = null;
      axiosInstance.post(apiUrl, payload).then(({ data, status, statusText }) => {
        if (status === 200) {
          if (userId === 0) {
            Swal.fire({
              title: payload.authenticationTypeId === 1 ? 'Login created' : 'Success',
              icon: 'success',
              html: payload.authenticationTypeId === 1 ? 'An email has been sent to the user\'s email address' : `A login creation link has been sent to the user's email address. This link will be valid for ${data} days.`,
              confirmButtonText: 'Manage users',
              showConfirmButton: true,
              showDenyButton: false,
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then((result) => {
              if (result.isConfirmed) {
                navigate('/users');
              }
            });
          } else {
            toast.success('User updated successfully');
            navigate('/users');
          }
        }
      });
    }
  };

  const convertToDropDownProps = (MasterItems: Array<IdNameViewModel>) => {
    const tmpMasterItems = _.cloneDeep(MasterItems);
    if (formValues.roleId === 2) {
      const allOfficesIndex = _.findIndex(tmpMasterItems, { id: 1 });
      if (allOfficesIndex !== -1) {
        tmpMasterItems.splice(allOfficesIndex, 1);
      }
    }
    return (tmpMasterItems || []).map((item: IdNameViewModel) => ({
      key: item.id,
      text: item.name,
      value: item?.id,
    } as DropdownItemProps));
  }

  const { masterData } = formValues;

  return (
    <section className="section" style={{
      minHeight: '100vh'
    }}>
      <div className="section__head">
        <div className="section__head-content">
          <Header as="h1">{userId === 0 ? 'Create' : 'Edit'} User</Header>
        </div>
        <div className="section__actions">
          <UserInfo />
          <SwitchTheme />
        </div>
      </div>
      <div className='section__body'>
        {
          <Dimmer.Dimmable as="div" dimmed={isLoading}>
            <Dimmer active={isLoading} inverted={true}>
              <Loader>Loading</Loader>
            </Dimmer>
            <Grid padded>
              <Grid.Row>
                <Grid.Column mobile={16} tablet={8} computer={8}>
                  <Form className='form-custom'>
                    <Form.Field className='required'>
                      <label>Role</label>
                      <Form.Group inline>
                        {masterData?.role.map(role => {
                          return (
                            <Form.Radio
                              key={role.id}
                              label={role.name}
                              value={role.id}
                              checked={formValues.roleId === role.id}
                              onChange={(e, data) => handleRadioButtonChange(e, data, 'roleId')}
                              onBlur={handleBlur}
                              style={{
                                margin: '1rem 1rem 0rem 1rem',
                                fontSize: '1.6rem'
                              }}
                            />
                          )
                        })}
                      </Form.Group>
                      {formErrors?.roleId && <div className="error-message" style={{ color: 'red' }}>{formErrors?.roleId}</div>}
                    </Form.Field>
                    <Form.Field className='required'>
                      <label>Authentication type</label>
                      <Form.Group inline>
                        {masterData?.authenticationType.map(authenticationType => {
                          return (
                            <Form.Radio
                              key={authenticationType.id}
                              label={authenticationType.name}
                              value={authenticationType.id}
                              checked={formValues.authenticationTypeId === authenticationType.id}
                              onChange={(e, data) => handleRadioButtonChange(e, data, 'authenticationTypeId')}
                              onBlur={handleBlur}
                              style={{
                                margin: '1rem 1rem 0rem 1rem',
                                fontSize: '1.6rem'
                              }}
                            />
                          )
                        })}
                      </Form.Group>
                      {formErrors?.authenticationTypeId && <div className="error-message" style={{ color: 'red' }}>{formErrors?.authenticationTypeId}</div>}
                    </Form.Field>
                    <Form.Field className='required'>
                      <label>Case assign option</label>
                      <Form.Group inline>
                        <Form.Radio
                          label='Yes'
                          value={1}
                          checked={formValues.isAssignToAllowed === true}
                          onChange={(e, data) => handleRadioButtonChange(e, data, 'isAssignToAllowed')}
                          onBlur={handleBlur}
                          style={{
                            margin: '1rem 1rem 0rem 1rem',
                            fontSize: '1.6rem'
                          }}
                        />
                        <Form.Radio
                          label='No'
                          value={0}
                          checked={formValues.isAssignToAllowed === false}
                          onChange={(e, data) => handleRadioButtonChange(e, data, 'isAssignToAllowed')}
                          onBlur={handleBlur}
                          style={{
                            margin: '1rem 1rem 0rem 1rem',
                            fontSize: '1.6rem'
                          }}
                        />
                      </Form.Group>
                      {formErrors?.authenticationTypeId && <div className="error-message" style={{ color: 'red' }}>{formErrors?.authenticationTypeId}</div>}
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        label="First name"
                        name="firstName"
                        value={formValues.firstName || ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.firstName}
                        required
                        maxLength={100}
                      />
                      {formValues.firstName?.length === 100 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                      {formErrors?.firstName && <div className="error-message" style={{ color: 'red' }}>{formErrors?.firstName}</div>}
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        label="Last name"
                        name="lastName"
                        value={formValues.lastName || ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.lastName}
                        required
                        maxLength={100}
                      />
                      {formValues.lastName?.length === 100 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                      {formErrors?.lastName && <div className="error-message" style={{ color: 'red' }}>{formErrors?.lastName}</div>}
                    </Form.Field>
                    <Form.Field className='email-field'>
                      <Form.Input
                        label="Email"
                        name="email"
                        value={formValues.email || ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.email}
                        required
                        maxLength={100}
                        style={{ marginBottom: '0.5rem' }}
                      />
                      <div>Your email address serves as your login name. Changing your email address will result in a change to your login name.</div>
                      {formValues.email?.length === 100 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                      {formErrors?.email && <div className="error-message" style={{ color: 'red' }}>{formErrors?.email}</div>}
                    </Form.Field>
                    <Form.Field>
                      <div className='required field'>
                        <label>Office</label>
                        <Dropdown
                          name="officeId"
                          options={convertToDropDownProps(masterData?.office || [])}
                          fluid
                          selection
                          clearable
                          value={formValues.officeId ? formValues.officeId : ''}
                          onChange={handleDropdownChange}
                          onBlur={handleDropdownBlur}
                          required
                          disabled={disabledOfficeDropdown}
                        />
                      </div>
                      {formErrors?.officeId && <div className="error-message" style={{ color: 'red' }}>{formErrors?.officeId}</div>}
                    </Form.Field>
                    <Form.Field>
                      <Checkbox
                        label='Active'
                        value={'true'}
                        checked={formValues.isActive === true}
                        onChange={(e, data) => handleCheckboxChange(e, data, 'isActive')}
                        onBlur={handleBlur}
                        style={{
                          margin: '1rem 1rem 0rem 1rem',
                          fontSize: '1.6rem'
                        }}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Checkbox
                        label='Subscribe to Email Updates'
                        value={'true'}
                        checked={formValues.notificationsAllowed === true}
                        onChange={(e, data) => handleCheckboxChange(e, data, 'notificationsAllowed')}
                        onBlur={handleBlur}
                        style={{
                          margin: '1rem 1rem 0rem 1rem',
                          fontSize: '1.6rem'
                        }}
                      />
                    </Form.Field>
                    <Grid>
                      <Grid.Column>
                        <Button type="submit" onClick={handleSubmit} className='violet right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>
                          {userId === 0 ? 'Create' : 'Update'}
                        </Button> {' '}
                        <Button type="button" className='second right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }} onClick={() => navigate('/users')}>
                          Cancel
                        </Button>
                      </Grid.Column>
                    </Grid>
                  </Form>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <br />
          </Dimmer.Dimmable>
        }
      </div>
    </section>
  );
}

export default UserForm;